import { CharacterStats, StatType } from '../../../../../shared/CharacterTypes';
import { calculateDerivedStats } from '../../../../../shared/logic/CombatLogic';
import { DERIVED_STAT_COEFFICIENTS as K, isDexScalingClass, isIntScalingClass } from '../../../../../shared/logic/derivedStatCoefficients';
import type { TooltipData } from '../../../components/Tooltip';

export type StatCategory = 'primary' | 'combat' | 'resource' | 'utility';
export type StatGameplayStatus = 'combat_effective' | 'display_only';

export interface StatDefinition {
  stat: StatType;
  abbreviation: string;
  label: string;
  category: StatCategory;
  gameplayStatus: StatGameplayStatus;
  shortDescription: string;
  fullDescription: string;
  formula: string;
  getBreakdown?: (stats: CharacterStats, characterClass?: string) => string;
}

/**
 * The stat-derived part of a rating, computed by the same `calculateDerivedStats`
 * the server uses. Gear and set bonuses come on top, so the sheet's current
 * value can be higher than this.
 */
function fromStats(stats: CharacterStats, cls?: string) {
  return calculateDerivedStats({
    strength: stats.strength || 0, dexterity: stats.dexterity || 0,
    intelligence: stats.intelligence || 0, luck: stats.luck || 0, level: 1, characterClass: cls,
  });
}

const DEX_CLASSES = 'Hunter/Rogue';
const pct = (n: number) => `${n}%`;

export const STAT_DEFINITIONS: Record<StatType, StatDefinition> = {
  [StatType.STRENGTH]: {
    stat: StatType.STRENGTH,
    abbreviation: 'STR',
    label: 'Strength',
    category: 'primary',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Contributes to physical attack and jump rating.',
    fullDescription: `Primary combat attribute. Grants +${K.attack.str} Physical Attack (+${K.attack.dexClassStr} for ${DEX_CLASSES}) and contributes to Jump rating.`,
    formula: `${DEX_CLASSES}: STR × ${K.attack.dexClassStr} | Other classes: STR × ${K.attack.str}`,
    getBreakdown: (stats, cls) => `STR (${stats.strength || 0}) × ${isDexScalingClass(cls) ? K.attack.dexClassStr : K.attack.str} ATK, × ${K.jump.str} JMP`,
  },
  [StatType.DEXTERITY]: {
    stat: StatType.DEXTERITY,
    abbreviation: 'DEX',
    label: 'Dexterity',
    category: 'primary',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Contributes to defense, accuracy, avoidability, speed, and jump rating.',
    fullDescription: `Agility attribute. Grants +${K.attack.dexClassDex} Physical Attack for ${DEX_CLASSES}, +${K.defense.dex} Physical Defense, +${K.accuracy.dex} Accuracy, +${K.avoidability.dex} Avoidability, and movement ratings.`,
    formula: `${DEX_CLASSES}: DEX × ${K.attack.dexClassDex} ATK | All: DEX × ${K.defense.dex} DEF, DEX × ${K.accuracy.dex} ACC, DEX × ${K.avoidability.dex} AVO`,
    getBreakdown: (stats) => `DEX (${stats.dexterity || 0}) contributes to DEF (×${K.defense.dex}), ACC (×${K.accuracy.dex}), AVO (×${K.avoidability.dex})`,
  },
  [StatType.INTELLIGENCE]: {
    stat: StatType.INTELLIGENCE,
    abbreviation: 'INT',
    label: 'Intelligence',
    category: 'primary',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Contributes to magic attack.',
    fullDescription: `Arcane attribute. Grants +${K.magicAttack.casterInt} Magic Attack for spellcasters (+${K.magicAttack.otherInt} for other classes).`,
    formula: `Casters: INT × ${K.magicAttack.casterInt} | Others: INT × ${K.magicAttack.otherInt}`,
    getBreakdown: (stats, cls) => `INT (${stats.intelligence || 0}) × ${isIntScalingClass(cls) ? K.magicAttack.casterInt : K.magicAttack.otherInt}`,
  },
  [StatType.LUCK]: {
    stat: StatType.LUCK,
    abbreviation: 'LUK',
    label: 'Luck',
    category: 'primary',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Contributes to magic defense, accuracy, avoidability, and critical rate.',
    fullDescription: `Fortune attribute. Grants +${K.magicDefense.luk} Magic Defense, +${K.criticalRate.luk}% Critical Strike chance, +${K.accuracy.luk} Accuracy, and +${K.avoidability.luk} Avoidability.`,
    formula: `LUK × ${K.magicDefense.luk} MDEF, LUK × ${K.criticalRate.luk}% CRIT, LUK × ${K.accuracy.luk} ACC, LUK × ${K.avoidability.luk} AVO`,
    getBreakdown: (stats) => `LUK (${stats.luck || 0}) contributes to MDEF (×${K.magicDefense.luk}), CRIT (+${((stats.luck || 0) * K.criticalRate.luk).toFixed(1)}%), ACC (×${K.accuracy.luk}), AVO (×${K.avoidability.luk})`,
  },
  [StatType.HEALTH]: {
    stat: StatType.HEALTH,
    abbreviation: 'HP',
    label: 'Health',
    category: 'resource',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Current health compared with the synced maximum.',
    fullDescription: 'Vitality pool. If this drops to zero, your character falls in battle.',
    formula: 'Class base health + level progression + gear stamina bonuses',
  },
  [StatType.MAX_HEALTH]: {
    stat: StatType.MAX_HEALTH,
    abbreviation: 'MAX HP',
    label: 'Maximum Health',
    category: 'resource',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Maximum health capacity.',
    fullDescription: 'Maximum health capacity determined by class and level, plus gear stamina bonuses.',
    formula: 'Class base health + level progression + gear stamina bonuses',
  },
  [StatType.MANA]: {
    stat: StatType.MANA,
    abbreviation: 'MP',
    label: 'Mana',
    category: 'resource',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Current mana compared with the synced maximum.',
    fullDescription: 'Magical resource consumed when executing abilities and spells.',
    formula: 'Class base mana + level progression + gear intellect bonuses',
  },
  [StatType.MAX_MANA]: {
    stat: StatType.MAX_MANA,
    abbreviation: 'MAX MP',
    label: 'Maximum Mana',
    category: 'resource',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Maximum mana capacity.',
    fullDescription: 'Maximum mana capacity determined by class and level, plus gear intellect bonuses.',
    formula: 'Class base mana + level progression + gear intellect bonuses',
  },
  [StatType.ATTACK]: {
    stat: StatType.ATTACK,
    abbreviation: 'ATK',
    label: 'Physical Attack',
    category: 'combat',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Synced physical attack power.',
    fullDescription: 'Primary scaling stat for all physical strikes, martial abilities, and weapon attacks.',
    formula: `${DEX_CLASSES}: STR × ${K.attack.dexClassStr} + DEX × ${K.attack.dexClassDex} | Other classes: STR × ${K.attack.str}`,
    getBreakdown: (stats, cls) => {
      const s = stats.strength || 0;
      const d = stats.dexterity || 0;
      const n = fromStats(stats, cls).attack;
      return isDexScalingClass(cls)
        ? `STR (${s}) × ${K.attack.dexClassStr} + DEX (${d}) × ${K.attack.dexClassDex} = ${n}`
        : `STR (${s}) × ${K.attack.str} = ${n}`;
    },
  },
  [StatType.MAGIC_ATTACK]: {
    stat: StatType.MAGIC_ATTACK,
    abbreviation: 'MATK',
    label: 'Magic Attack',
    category: 'combat',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Synced magic attack power.',
    fullDescription: 'Primary scaling stat for spells, elemental damage, and magical healing.',
    formula: `Caster classes: INT × ${K.magicAttack.casterInt} | Other classes: INT × ${K.magicAttack.otherInt}`,
    getBreakdown: (stats, cls) => {
      const mult = isIntScalingClass(cls) ? K.magicAttack.casterInt : K.magicAttack.otherInt;
      return `INT (${stats.intelligence || 0}) × ${mult} = ${fromStats(stats, cls).magicAttack}`;
    },
  },
  [StatType.DEFENSE]: {
    stat: StatType.DEFENSE,
    abbreviation: 'DEF',
    label: 'Physical Defense',
    category: 'combat',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Physical mitigation rating used by the combat damage curve.',
    fullDescription: 'Reduces incoming physical damage according to the combat mitigation curve.',
    formula: `DEX × ${K.defense.dex}`,
    getBreakdown: (stats, cls) => `DEX (${stats.dexterity || 0}) × ${K.defense.dex} = ${fromStats(stats, cls).defense}`,
  },
  [StatType.MAGIC_DEFENSE]: {
    stat: StatType.MAGIC_DEFENSE,
    abbreviation: 'MDEF',
    label: 'Magic Defense',
    category: 'combat',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Magic mitigation rating used by the combat damage curve.',
    fullDescription: 'Reduces incoming magical damage according to the combat mitigation curve.',
    formula: `LUK × ${K.magicDefense.luk}`,
    getBreakdown: (stats, cls) => `LUK (${stats.luck || 0}) × ${K.magicDefense.luk} = ${fromStats(stats, cls).magicDefense}`,
  },
  [StatType.CRITICAL_RATE]: {
    stat: StatType.CRITICAL_RATE,
    abbreviation: 'CRIT',
    label: 'Critical Rate',
    category: 'combat',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Synced chance for an attack to critically hit.',
    fullDescription: 'Chance for offensive strikes to critically strike for increased damage.',
    formula: `${DEX_CLASSES}: ${pct(K.criticalRate.base)} + LUK × ${pct(K.criticalRate.luk)} + DEX × ${pct(K.criticalRate.dexClassDex)} | Others: ${pct(K.criticalRate.base)} + LUK × ${pct(K.criticalRate.luk)}`,
    getBreakdown: (stats, cls) => {
      const l = stats.luck || 0;
      const d = stats.dexterity || 0;
      const n = pct(fromStats(stats, cls).criticalRate);
      return isDexScalingClass(cls)
        ? `Base ${pct(K.criticalRate.base)} + LUK (${l}) × ${pct(K.criticalRate.luk)} + DEX (${d}) × ${pct(K.criticalRate.dexClassDex)} = ${n}`
        : `Base ${pct(K.criticalRate.base)} + LUK (${l}) × ${pct(K.criticalRate.luk)} = ${n}`;
    },
  },
  [StatType.CRITICAL_DAMAGE]: {
    stat: StatType.CRITICAL_DAMAGE,
    abbreviation: 'CRIT DMG',
    label: 'Critical Damage',
    category: 'combat',
    gameplayStatus: 'combat_effective',
    shortDescription: 'Critical-hit multiplier from class and active bonuses.',
    fullDescription: 'Damage multiplier applied when landing a critical strike.',
    formula: 'Class base multiplier + equipment + set bonuses',
  },
  [StatType.ACCURACY]: {
    stat: StatType.ACCURACY,
    abbreviation: 'ACC',
    label: 'Accuracy',
    category: 'utility',
    gameplayStatus: 'display_only',
    shortDescription: 'Synced hit rating derived from Dexterity and Luck.',
    fullDescription: 'Hit rating derived from Dexterity and Luck. Displayed as a character rating.',
    formula: `DEX × ${K.accuracy.dex} + LUK × ${K.accuracy.luk}`,
    getBreakdown: (stats, cls) =>
      `DEX (${stats.dexterity || 0}) × ${K.accuracy.dex} + LUK (${stats.luck || 0}) × ${K.accuracy.luk} = ${fromStats(stats, cls).accuracy}`,
  },
  [StatType.AVOIDABILITY]: {
    stat: StatType.AVOIDABILITY,
    abbreviation: 'AVO',
    label: 'Avoidability',
    category: 'utility',
    gameplayStatus: 'display_only',
    shortDescription: 'Synced dodge rating derived from Dexterity and Luck.',
    fullDescription: 'Evasion rating derived from Luck and Dexterity. Displayed as a character rating.',
    formula: `LUK × ${K.avoidability.luk} + DEX × ${K.avoidability.dex}`,
    getBreakdown: (stats, cls) =>
      `LUK (${stats.luck || 0}) × ${K.avoidability.luk} + DEX (${stats.dexterity || 0}) × ${K.avoidability.dex} = ${fromStats(stats, cls).avoidability}`,
  },
  [StatType.SPEED]: {
    stat: StatType.SPEED,
    abbreviation: 'SPD',
    label: 'Speed',
    category: 'utility',
    gameplayStatus: 'display_only',
    shortDescription: 'Synced movement rating; Dexterity contributes.',
    fullDescription: `Movement rating. Base ${K.speed.base} with Dexterity contribution. Displayed as a character rating.`,
    formula: `Base ${K.speed.base} + DEX × ${K.speed.dex}`,
    getBreakdown: (stats, cls) => `Base ${K.speed.base} + DEX (${stats.dexterity || 0}) × ${K.speed.dex} = ${fromStats(stats, cls).speed}`,
  },
  [StatType.JUMP]: {
    stat: StatType.JUMP,
    abbreviation: 'JMP',
    label: 'Jump',
    category: 'utility',
    gameplayStatus: 'display_only',
    shortDescription: 'Synced jump rating; Strength and Dexterity contribute.',
    fullDescription: `Jump power rating. Base ${K.jump.base} with Strength and Dexterity contribution. Displayed as a character rating.`,
    formula: `Base ${K.jump.base} + STR × ${K.jump.str} + DEX × ${K.jump.dex}`,
    getBreakdown: (stats, cls) =>
      `Base ${K.jump.base} + STR (${stats.strength || 0}) × ${K.jump.str} + DEX (${stats.dexterity || 0}) × ${K.jump.dex} = ${fromStats(stats, cls).jump}`,
  },
};

export const getStatAbbreviation = (stat: StatType): string => {
  return STAT_DEFINITIONS[stat]?.abbreviation ?? stat;
};

export const getStatDescription = (stat: StatType): string => {
  return STAT_DEFINITIONS[stat]?.shortDescription ?? 'Synced character statistic.';
};

export function formatStatValue(value: number | undefined, stat?: StatType): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  if (stat === StatType.CRITICAL_RATE) {
    return `${Number(value.toFixed(2))}%`;
  }
  if (stat === StatType.CRITICAL_DAMAGE) {
    return `${Number(value.toFixed(2))}×`;
  }
  return String(Math.round(value));
}

export function formatResourceValue(current: number | undefined, max: number | undefined): string {
  if (current === undefined || max === undefined || !Number.isFinite(current) || !Number.isFinite(max)) {
    return '—';
  }
  return `${Math.round(current)} / ${Math.round(max)}`;
}

export function createStatTooltipData(
  stat: StatType,
  value: number | undefined,
  stats?: CharacterStats,
  characterClass?: string,
  statPoints?: number,
): TooltipData {
  const def = STAT_DEFINITIONS[stat];
  const formattedVal = formatStatValue(value, stat);
  const statusLabel = def?.gameplayStatus === 'combat_effective' ? 'Combat Effective' : 'Rating / Display Only';
  const breakdown = def?.getBreakdown && stats ? def.getBreakdown(stats, characterClass) : def?.formula;

  return {
    title: `${def?.label ?? stat} (${def?.abbreviation ?? stat})`,
    subtitle: `${statusLabel} · Current: ${formattedVal}`,
    description: def?.fullDescription ?? def?.shortDescription ?? getStatDescription(stat),
    extraInfo: breakdown ? `Formula: ${breakdown}` : undefined,
    interactionHint: statPoints !== undefined && statPoints > 0 && def?.category === 'primary'
      ? `${statPoints} stat point${statPoints > 1 ? 's' : ''} available to allocate`
      : undefined,
  };
}
